import _ from "lodash";
import {
  getAdvoAssistRepresentative,
  isContractProduct,
} from "../../../services/Product/ProductService/productService";
import { translate } from "../../../services/Translations/translatorService";
import { isLitigationCase } from "../../../types/AbstractCase";
import { Address } from "../../../types/Address";
import { Person } from "../../../types/Person";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { BackofficeUser } from "../../../types/BackofficeUser";
import { Editor } from "@tiptap/react";
import { CasePdfGeneratorFormValues } from "../../../types/CasePdfGenerator";
import { CasePerson } from "../../../types/CasePerson";
import { ProductUnion } from "../../../types/ProductUnion";
import { CaseOrganization } from "../../../types/CaseOrganization";
import dayjs from "dayjs";

const isOpponentLawyerDataComplete = (product: ProductUnion) => {
  // Required fields for opponentLawFirm. name is NOT required
  return !!(
    product.opponentLawyer &&
    product.opponentLawFirm &&
    product.opponentLawyer.gender &&
    product.opponentLawyer.givenName &&
    product.opponentLawyer.familyName &&
    product.opponentLawFirm.organization.address &&
    isAddressDataComplete(product.opponentLawFirm.organization.address)
  );
};

const isDebtCollectionAgencyDataComplete = (product: ProductUnion) => {
  return !!(
    product.debtCollectionAgency &&
    product.debtCollectionAgency.organization.name &&
    product.debtCollectionAgency.organization.address &&
    isAddressDataComplete(product.debtCollectionAgency.organization.address)
  );
};

const isContractualPartnerPersonDataComplete = (product: ProductUnion) => {
  // Required fields for contractualPartnerPerson
  return !!(
    product.contractualPartnerPerson &&
    product.contractualPartnerPerson.gender &&
    product.contractualPartnerPerson.givenName &&
    product.contractualPartnerPerson.familyName &&
    product.contractualPartnerPerson.addresses[0] &&
    isAddressDataComplete(product.contractualPartnerPerson.addresses[0])
  );
};

const isOpposingCarLiabilityComplete = (product: ProductUnion) => {
  return product.opposingInsurancePolicyNumber && product.opposingCaseReferenceNumber;
};

const isPropertyManagerDataComplete = (product: ProductUnion) => {
  return product.propertyManager && isAddressDataComplete(product.propertyManager.organization.address);
};

const isContractualPartnerLegalEntityDataComplete = (product: ProductUnion) => {
  // Required fields for contractualPartnerLegalEntity --> No streetAddress required!
  return !!(
    product.contractualPartnerLegalEntity &&
    product.contractualPartnerLegalEntity.organization.legalName &&
    product.contractualPartnerLegalEntity.organization.address &&
    (isAddressDataComplete(product.contractualPartnerLegalEntity.organization.address, true) ||
      isPostOfficeBoxDataComplete(product.contractualPartnerLegalEntity.organization.address))
  );
};

const isAddressDataComplete = (address: Address, isStreetAddressOptional = false) => {
  return (isStreetAddressOptional || address.streetAddress) && address.addressLocality && address.postalCode;
};

const isPostOfficeBoxDataComplete = (address: Address) =>
  address.postOfficeBoxNumber && address.postOfficeBoxPostalCode && address.postOfficeBoxAddressLocality;

const isContractualPartnerDataComplete = (product: ProductUnion) => {
  if (product.opposingPartyType === "opposingPartyLegalEntity") {
    return isContractualPartnerLegalEntityDataComplete(product);
  }
  if (product.opposingPartyType === "opposingPartyPerson") {
    return isContractualPartnerPersonDataComplete(product);
  }
};

const getContractualPartnerPersonLetterheadData = (product: ProductUnion) => {
  return {
    gender: product.contractualPartnerPerson.gender,
    name: product.contractualPartnerPerson.givenName + " " + product.contractualPartnerPerson.familyName,
    streetAddress: product.contractualPartnerPerson.residenceAddress.streetAddress,
    postalCode: product.contractualPartnerPerson.residenceAddress.postalCode,
    addressLocality: product.contractualPartnerPerson.residenceAddress.addressLocality,
    postOfficeBoxAddressLocality: product.contractualPartnerPerson.residenceAddress.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.contractualPartnerPerson.residenceAddress.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.contractualPartnerPerson.residenceAddress.postOfficeBoxPostalCode,
  };
};

const getOrganizationLetterheadData = (path: string, product: ProductUnion) => {
  return {
    firm: _.get(product, path + ".organization.legalName"),
    streetAddress: _.get(product, path + ".organization.address.streetAddress"),
    addressAddition: _.get(product, path + ".organization.address.addressAddition"),
    postalCode: _.get(product, path + ".organization.address.postalCode"),
    addressLocality: _.get(product, path + ".organization.address.addressLocality"),
    postOfficeBoxAddressLocality: _.get(product, path + ".organization.address.postOfficeBoxAddressLocality"),
    postOfficeBoxNumber: _.get(product, path + ".organization.address.postOfficeBoxNumber"),
    postOfficeBoxPostalCode: _.get(product, path + ".organization.address.postOfficeBoxPostalCode"),
  };
};

const getContractualPartnerLetterheadData = (product: ProductUnion) => {
  if (product.opposingPartyType === "opposingPartyLegalEntity") {
    return getOrganizationLetterheadData("contractualPartnerLegalEntity", product);
  }
  if (product.opposingPartyType === "opposingPartyPerson") {
    return getContractualPartnerPersonLetterheadData(product);
  }
};

const getAuthorityLetterheadData = (product: ProductUnion) => {
  return {
    name: product.responsibleAuthority.name,
    department: product.responsibleAuthority.department,
    subDepartment: product.responsibleAuthority.subDepartment,
    streetAddress: product.responsibleAuthority.address.streetAddress,
    postalCode: product.responsibleAuthority.address.postalCode,
    addressLocality: product.responsibleAuthority.address.addressLocality,
    postOfficeBoxAddressLocality: product.responsibleAuthority.address.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.responsibleAuthority.address.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.responsibleAuthority.address.postOfficeBoxPostalCode,
  };
};

const getProsecutionLetterheadData = (product: ProductUnion) => {
  return {
    name: product.responsibleProsecution.name,
    department: product.responsibleProsecution.department,
    subDepartment: product.responsibleProsecution.subDepartment,
    streetAddress: product.responsibleProsecution.address.streetAddress,
    postalCode: product.responsibleProsecution.address.postalCode,
    addressLocality: product.responsibleProsecution.address.addressLocality,
    postOfficeBoxAddressLocality: product.responsibleProsecution.address.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.responsibleProsecution.address.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.responsibleProsecution.address.postOfficeBoxPostalCode,
  };
};

const getPetitionerLetterheadData = (product: ProductUnion) => {
  return {
    gender: _.get(product, "client.gender"),
    name: _.get(product, "client.givenName") + " " + _.get(product, "client.familyName"),
    streetAddress: _.get(product, "client.residenceAddress.streetAddress"),
    postalCode: _.get(product, "client.residenceAddress.postalCode"),
    addressLocality: _.get(product, "client.residenceAddress.addressLocality"),
    postOfficeBoxAddressLocality: _.get(product, "client.residenceAddress.postOfficeBoxAddressLocality"),
    postOfficeBoxNumber: _.get(product, "client.residenceAddress.postOfficeBoxNumber"),
    postOfficeBoxPostalCode: _.get(product, "client.residenceAddress.postOfficeBoxPostalCode"),
  };
};

const getProcessParticipantsPeopleLetterheadData = (person: Person, address: Address) => {
  return {
    gender: _.get(person, "gender"),
    name: _.get(person, "givenName") + " " + _.get(person, "familyName"),
    streetAddress: _.get(address, "streetAddress"),
    postalCode: _.get(address, "postalCode"),
    addressLocality: _.get(address, "addressLocality"),
    postOfficeBoxAddressLocality: _.get(address, "postOfficeBoxAddressLocality"),
    postOfficeBoxNumber: _.get(address, "postOfficeBoxNumber"),
    postOfficeBoxPostalCode: _.get(address, "postOfficeBoxPostalCode"),
  };
};

const getInsuranceLetterheadData = (product: ProductUnion) => {
  if (!product.caseInsurance.insurer) {
    return;
  }

  return {
    firm: product.caseInsurance.insurer.name,
    streetAddress: product.caseInsurance.insurer.address?.streetAddress,
    postalCode: product.caseInsurance.insurer.address?.postalCode,
    addressLocality: product.caseInsurance.insurer.address?.addressLocality,
    postOfficeBoxAddressLocality: product.caseInsurance.insurer.address?.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.caseInsurance.insurer.address?.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.caseInsurance.insurer.address?.postOfficeBoxPostalCode,
  };
};

const getEmployerOrganizationLetterheadData = (product: ProductUnion) => {
  return {
    firm: product.employerOrganization.name,
    streetAddress: product.employerOrganization.address.streetAddress,
    postalCode: product.employerOrganization.address.postalCode,
    addressLocality: product.employerOrganization.address.addressLocality,
    postOfficeBoxAddressLocality: product.employerOrganization.address.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.employerOrganization.address.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.employerOrganization.address.postOfficeBoxPostalCode,
  };
};

const getCourtLetterheadData = (product: ProductUnion) => {
  return {
    firm: product.responsibleCourt.name,
    streetAddress: product.responsibleCourt.address.streetAddress,
    postalCode: product.responsibleCourt.address.postalCode,
    addressLocality: product.responsibleCourt.address.addressLocality,
    postOfficeBoxAddressLocality: product.responsibleCourt.address.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.responsibleCourt.address.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.responsibleCourt.address.postOfficeBoxPostalCode,
  };
};

const getOpponentLawFirmLetterheadData = (product: ProductUnion) => {
  return {
    firm: product.opponentLawFirm.organization.name ?? null,
    name:
      (product.opponentLawyer.gender === "female" ? "RAin " : "RA ") +
      product.opponentLawyer.givenName +
      " " +
      product.opponentLawyer.familyName,
    streetAddress: product.opponentLawFirm.organization.address.streetAddress,
    postalCode: product.opponentLawFirm.organization.address.postalCode,
    addressLocality: product.opponentLawFirm.organization.address.addressLocality,
    postOfficeBoxAddressLocality: product.opponentLawFirm.organization.address.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.opponentLawFirm.organization.address.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.opponentLawFirm.organization.address.postOfficeBoxPostalCode,
  };
};

const getDebtCollectionAgencyLetterheadData = (product: ProductUnion) => {
  return {
    name: product.debtCollectionAgency.organization.name,
    streetAddress: product.debtCollectionAgency.organization.address.streetAddress,
    postalCode: product.debtCollectionAgency.organization.address.postalCode,
    addressLocality: product.debtCollectionAgency.organization.address.addressLocality,
    postOfficeBoxAddressLocality: product.debtCollectionAgency.organization.address.postOfficeBoxAddressLocality,
    postOfficeBoxNumber: product.debtCollectionAgency.organization.address.postOfficeBoxNumber,
    postOfficeBoxPostalCode: product.debtCollectionAgency.organization.address.postOfficeBoxPostalCode,
  };
};

const getAdvoAssistRepresentativeLetterheadData = (product: ProductUnion) => {
  const advoAssistRepresentative = getAdvoAssistRepresentative(product);

  if (!advoAssistRepresentative) {
    return {};
  }

  return {
    name: advoAssistRepresentative.fullName,
    streetAddress: advoAssistRepresentative.residenceAddress.streetAddress,
    postalCode: advoAssistRepresentative.residenceAddress.postalCode,
    addressLocality: advoAssistRepresentative.residenceAddress.addressLocality,
  };
};

const buildAddress = (values: Record<string, any>, product: ProductUnion) => {
  const baseData = {
    firm: null,
    gender: null,
    name: null,
    department: null,
    subDepartment: null,
    streetAddress: null,
    addressAddition: null,
    postalCode: null,
    addressLocality: null,
    postOfficeBoxNumber: null,
    postOfficeBoxPostalCode: null,
    postOfficeBoxAddressLocality: null,
  };

  const recipientType = values.recipientType.split("[")[0];

  if (recipientType.startsWith("processParticipantsPeople")) {
    const selectedPerson = product.processParticipantsPeople?.find(
      (participant: Person) => participant.id + "" === recipientType.split(".")[1]
    );
    const address = selectedPerson.addresses[0];
    return _.merge({}, baseData, getProcessParticipantsPeopleLetterheadData(selectedPerson, address));
  }

  switch (recipientType) {
    case "custom":
      return _.merge({}, baseData, values.address);
    case "petitioner":
      return _.merge({}, baseData, getPetitionerLetterheadData(product));
    case "court":
      return _.merge({}, baseData, getCourtLetterheadData(product));
    case "authority":
      return _.merge({}, baseData, getAuthorityLetterheadData(product));
    case "prosecution":
      return _.merge({}, baseData, getProsecutionLetterheadData(product));
    case "contractualPartnerLegalEntity":
      return _.merge({}, baseData, getContractualPartnerLetterheadData(product));
    case "opponentLawFirm":
      return _.merge({}, baseData, getOpponentLawFirmLetterheadData(product));
    case "debtCollectionAgency":
      return _.merge({}, baseData, getDebtCollectionAgencyLetterheadData(product));
    case "advoAssistRepresentative":
      return _.merge({}, baseData, getAdvoAssistRepresentativeLetterheadData(product));
    case "processParticipantsOrganizations":
      return _.merge({}, baseData, getOrganizationLetterheadData(values.recipientType, product));
    case "insurance":
      return _.merge({}, baseData, getInsuranceLetterheadData(product));
    case "opposingCarLiabilityInsurance":
      return _.merge({}, baseData, getOrganizationLetterheadData("opposingCarLiabilityInsurance", product));
    case "opposingLiabilityInsurance":
      return _.merge({}, baseData, getOrganizationLetterheadData("opposingLiabilityInsurance", product));
    case "employerOrganization":
      return _.merge({}, baseData, getEmployerOrganizationLetterheadData(product));
    case "propertyManager":
      return _.merge({}, baseData, getOrganizationLetterheadData("propertyManager", product));

    default:
      return baseData;
  }
};

const getExternalReference = (product: ProductUnion, switchValue: string) => {
  if (switchValue.startsWith("processParticipantsPeople")) {
    return product.processParticipantsPeople.find(
      (participant: Person) => participant.id + "" === switchValue.split(".")[1]
    ).casePerson.reference;
  }

  switch (product.productClassName) {
    case "traffic":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "authority":
          return product.fineNoticeReference;
        case "prosecution":
          return product.prosecutionReference;
        default:
          return null;
      }
    case "settlement":
    case "divorce":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "opponentLawyer":
          return product.referenceOpponentLawyer;
        default:
          return null;
      }
    case "trafficAccident":
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "dataLeakContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
    case "employmentContract":
      switch (switchValue) {
        case "debtCollectionAgency":
          return product.debtCollectionAgencyReference;
        case "court":
          return product.courtReference;
        case "opponentLawyer":
          return product.referenceOpponentLawyer;
        case "contractNumber":
          return product.contractNumber;
        case "prosecution":
          return product.prosecutionReference;
        case "authority":
          return product.authorityReference;
        case "opposingPartyReference":
          return product.opposingPartyReference;
        default:
          return null;
      }
    case "generalCivilLaw":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "opponentLawyer":
          return product.referenceOpponentLawyer;
        case "policeReportReferenceNumber":
          return product.policeReportReferenceNumber;
        case "prosecution":
          return product.prosecutionReference;
        case "opposingPartyReference":
          return product.opposingPartyReference;
        default:
          return null;
      }
    case "civilServiceLaw":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "opponentLawyer":
          return product.referenceOpponentLawyer;
        case "policeReportReferenceNumber":
          return product.policeReportReferenceNumber;
        case "prosecution":
          return product.prosecutionReference;
        case "authority":
          return product.authorityReference;
        default:
          return null;
      }
    case "rentalContract":
      switch (switchValue) {
        case "debtCollectionAgency":
          return product.debtCollectionAgencyReference;
        case "propertyManager":
          return product.propertyManagerReference;
        case "opponentLawyer":
          return product.referenceOpponentLawyer;
        case "court":
          return product.courtReference;
        case "prosecution":
          return product.prosecutionReference;
        case "opposingPartyReference":
          return product.opposingPartyReference;
        default:
          return null;
      }
    default:
      return null;
  }
};

const getExternalReferenceSelectValue = (product: ProductUnion, recipientType: string) => {
  const defaultFieldName = "free";

  if (recipientType.startsWith("processParticipantsPeople")) {
    const index = recipientType.split(".")[1];
    return "processParticipantsPeopleReference." + index;
  }

  switch (product.productClassName) {
    case "traffic":
      switch (recipientType) {
        case "authority":
          return product.fineNoticeReference ? "authority" : defaultFieldName;
      }
    // fallthrough
    case "rentalContract":
      switch (recipientType) {
        case "propertyManager":
          return product.propertyManagerReference ? "propertyManager" : defaultFieldName;
      }
    // fallthrough
    default:
      switch (recipientType) {
        case "court":
          return product.courtReference ? "court" : defaultFieldName;
        case "contractualPartnerLegalEntity":
          if (product.opposingPartyReference) {
            return "opposingPartyReference";
          } else if (product.contractNumber) {
            return "contractNumber";
          }

          return defaultFieldName;
        case "opponentLawyer":
        case "opponentLawFirm":
          return product.referenceOpponentLawyer ? "opponentLawyer" : defaultFieldName;
        case "debtCollectionAgency":
          return product.debtCollectionAgencyReference ? "debtCollectionAgency" : defaultFieldName;
        case "authority":
          return product.authorityReference ? "authority" : defaultFieldName;
        case "prosecution":
          return product.prosecutionReference ? "prosecution" : defaultFieldName;
        case "custom":
        case "insurance":
        case "opposingCarLiabilityInsurance":
        case "opposingLiabilityInsurance":
        case "petitioner":
        case "advoAssistRepresentative":
        default:
          return defaultFieldName;
      }
  }
};

const getReferenceOptions = (product: ProductUnion) => {
  let references = [];

  switch (product.productClassName) {
    case "settlement":
    case "divorce":
      references.push(
        {
          type: "court",
          label: "Aktenzeichen Gericht",
          isHidden: !product.courtReference,
        },
        {
          type: "opponentLawyer",
          label: "Aktenzeichen Anwalt Gegenseite",
          isHidden: !product.referenceOpponentLawyer,
        },
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    case "traffic":
      references.push(
        {
          type: "court",
          label: "Aktenzeichen Gericht",
          isHidden: !product.courtReference,
        },
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.fineNoticeReference,
        },
        {
          type: "prosecution",
          label: "Aktenzeichen Staatsanwaltschaft",
          isHidden: !product.prosecutionReference,
        },
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    case "trafficAccident":
      references.push(
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.authorityReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "dataLeakContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
    case "employmentContract":
      references.push(
        {
          type: "debtCollectionAgency",
          label: "Aktenzeichen Inkasso",
          isHidden: !product.debtCollectionAgencyReference,
        },
        {
          type: "contractNumber",
          label: "Vertragsnummer",
          isHidden: !product.contractNumber,
        },
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.authorityReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    case "generalCivilLaw":
      references.push(
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    case "civilServiceLaw":
      references.push(
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.authorityReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    case "rentalContract":
      references.push(
        {
          type: "propertyManager",
          label: "Aktenzeichen Hausverwaltung",
          isHidden: !product.propertyManagerReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product)
      );
      break;
    default:
      break;
  }

  return references;
};

const getLitigationReferenceOptions = (product: ProductUnion) => {
  return [
    {
      type: "opponentLawyer",
      label: "Aktenzeichen Anwalt Gegenseite",
      isHidden: !product.referenceOpponentLawyer,
    },
    {
      type: "court",
      label: "Aktenzeichen Gericht",
      isHidden: !product.courtReference,
    },
    {
      type: "policeReportReferenceNumber",
      label: "Aktenzeichen Polizei",
      isHidden: !product.policeReportReferenceNumber,
    },
    {
      type: "prosecution",
      label: "Aktenzeichen Staatsanwaltschaft",
      isHidden: !product.prosecutionReference,
    },
    {
      type: "opposingPartyReference",
      label: "Internes Aktenzeichen Gegenseite",
      isHidden: !product.opposingPartyReference,
    },
  ];
};

function hasCasePersonReference(p: Person): p is Person & { casePerson: CasePerson } {
  return Boolean(p.casePerson?.reference);
}

const getProcessParticipantsPeopleReferenceOptions = (product: ProductUnion) => {
  //cast won't be needed anymore as soon as the product types are correctly implemented
  return (product.processParticipantsPeople as Person[]).filter(hasCasePersonReference).map((person) => {
    return {
      type: "processParticipantsPeopleReference." + person.id,
      label: "Aktenzeichen " + translate("participant.labelType.values." + person.labelType),
      isHidden: false,
    };
  });
};

const getPdfGeneratorRecipients = (product: ProductUnion) => {
  let recipients = [];
  if (product.caseInsurance.insurer) {
    recipients.push({ value: "insurance", label: "Rechtsschutzversicherung" });
  }
  if (
    product.productClassName === "trafficAccident" &&
    product.opposingCarLiabilityInsurance &&
    isOpposingCarLiabilityComplete(product)
  ) {
    recipients.push({ value: "opposingCarLiabilityInsurance", label: "KFZ-Haftpflicht Gegenseite" });
  }
  if (isLitigationCase(product) && product.opposingLiabilityInsurance) {
    recipients.push({ value: "opposingLiabilityInsurance", label: "Haftpflichtversicherung Gegenseite" });
  }
  if (product.responsibleCourt) {
    recipients.push({ value: "court", label: "Zuständiges Gericht" });
  }
  if (product.responsibleAuthority) {
    recipients.push({ value: "authority", label: "Zuständige Behörde" });
  }
  if (product.responsibleProsecution) {
    recipients.push({ value: "prosecution", label: "Zuständige Staatsanwaltschaft" });
  }
  if (product.employerOrganization) {
    recipients.push({ value: "employerOrganization", label: "Adresse Gegenseite" });
  }

  recipients.push({ value: "petitioner", label: "Mandantschaft" });

  if (
    (isContractProduct(product.productClassName) ||
      product.productCategory === "civilLaw" ||
      product.productCategory === "employmentLaw") &&
    isContractualPartnerDataComplete(product)
  ) {
    recipients.push({ value: "contractualPartnerLegalEntity", label: "Gegenseite" });
  }

  if (product.productClassName === "rentalContract" && isPropertyManagerDataComplete(product)) {
    recipients.push({ value: "propertyManager", label: "Hausverwaltung" });
  }
  if (
    (isContractProduct(product.productClassName) ||
      ["settlement", "divorce", "rentalContract", "generalCivilLaw", "employmentContract", "civilServiceLaw"].includes(
        product.productClassName
      )) &&
    isOpponentLawyerDataComplete(product)
  ) {
    recipients.push({ value: "opponentLawFirm", label: "Anwalt Gegenseite" });
  }
  if (
    (isContractProduct(product.productClassName) || product.productClassName === "employmentContract") &&
    isDebtCollectionAgencyDataComplete(product)
  ) {
    recipients.push({ value: "debtCollectionAgency", label: "Inkassobüro" });
  }
  if (getAdvoAssistRepresentative(product)) {
    recipients.push({ value: "advoAssistRepresentative", label: "Terminsvertreter" });
  }
  if (!!product.processParticipantsPeople && product.processParticipantsPeople.length > 0) {
    product.processParticipantsPeople.forEach((person: Person) =>
      recipients.push({
        value: "processParticipantsPeople." + person.id,
        label:
          translate("participant.labelType.values." + person.labelType) +
          " - " +
          person.givenName +
          " " +
          person.familyName,
      })
    );
  }
  if (!!product.processParticipantsOrganizations && product.processParticipantsOrganizations.length > 0) {
    product.processParticipantsOrganizations.forEach((caseOrganization: CaseOrganization, index: number) =>
      recipients.push({
        value: `processParticipantsOrganizations[${index}]`,
        label:
          translate("participant.labelType.values." + caseOrganization.organization.labelType) +
          " - " +
          caseOrganization.organization.legalName,
      })
    );
  }
  recipients.push({ value: "custom", label: "Empfänger frei wählen" });

  return recipients;
};

const getCasePdfFormParams = ({ values, editor, product, currentUser }: casePdfFormParamsProps) => {
  return {
    ...values,
    content: getEditorHtml(editor),
    address: buildAddress(values, product),
    externalReference: values.externalReference?.trim() ? values.externalReference : null,
    senderMedium: values.senderMedium === "none" ? null : values.senderMedium,
    externalLawyer: currentUser.isExternal ? currentUser : null,
    insurancePolicyNumber: ["insurance", "opposingCarLiabilityInsurance", "opposingLiabilityInsurance"].includes(
      values.recipientType
    )
      ? values.insurancePolicyNumber
      : null,
    caseReferenceNumber: ["insurance", "opposingCarLiabilityInsurance", "opposingLiabilityInsurance"].includes(
      values.recipientType
    )
      ? values.caseReferenceNumber
      : null,
  };
};

const getDocumentName = (recipientType: string) => {
  const date = dayjs().format("YYYYMMDD");
  const documentName = "_Schreiben";
  let documentSuffix = "";

  switch (recipientType) {
    case "insurance":
      documentSuffix = "_aRSV";
      break;
    case "opposingCarLiabilityInsurance":
      documentSuffix = "_aGgs";
      break;
    case "opposingLiabilityInsurance":
      documentSuffix = "_aHPVGs";
      break;
    case "court":
      documentSuffix = "_aG";
      break;
    case "contractualPartnerLegalEntity":
      documentSuffix = "_aGs";
      break;
    case "petitioner":
      documentSuffix = "_aM";
      break;
    case "advoAssistRepresentative":
      documentSuffix = "_aTV";
      break;
    case "opponentLawyer":
    case "opponentLawFirm":
      documentSuffix = "_agRA";
      break;
    case "debtCollectionAgency":
      documentSuffix = "_aI";
      break;
    case "propertyManager":
      documentSuffix = "_aHv";
      break;
    case "authority":
      documentSuffix = "_aB";
      break;
    case "prosecution":
      documentSuffix = "_aSta";
      break;
    default:
      break;
  }

  return date + documentName + documentSuffix;
};

type casePdfFormParamsProps = {
  values: Omit<CasePdfGeneratorFormValues, "content">;
  editor: Editor | null;
  product: ProductUnion;
  currentUser: BackofficeUser;
};

export {
  isOpponentLawyerDataComplete,
  isContractualPartnerDataComplete,
  buildAddress,
  getExternalReference,
  getExternalReferenceSelectValue,
  isDebtCollectionAgencyDataComplete,
  getReferenceOptions,
  getPdfGeneratorRecipients,
  getDocumentName,
  getCasePdfFormParams,
};
